import { createContext, useContext, useState, ReactNode, useRef, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { CircleCheckIcon, CircleXIcon, InfoIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

const POPUP_STYLES = {
  ERROR: {
    color: 'red',
    icon: <CircleXIcon size={20} />,
    text: <FormattedMessage id='alert.error' />,
  },
  SUCCESS: {
    color: 'green',
    icon: <CircleCheckIcon size={20} />,
    text: <FormattedMessage id='alert.success' />,
  },
  INFO: {
    color: 'blue',
    icon: <InfoIcon size={20} />,
    text: <FormattedMessage id='alert.info' />,
  },
};

interface Popup {
  message: string;
  type: 'INFO' | 'SUCCESS' | 'ERROR';
}

interface PopupContextType {
  showPopup: (options: Popup) => void;
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export const PopupProvider = ({ children }: { children: ReactNode }) => {
  const [queue, setQueue] = useState<Popup[]>([]);
  const [currentPopup, setCurrentPopup] = useState<Popup | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen && !currentPopup && queue.length > 0) {
      const nextPopup = queue[0];
      setQueue((prev) => prev.slice(1));
      setCurrentPopup(nextPopup);
      setIsOpen(true);
    }
  }, [queue, isOpen, currentPopup]);

  const showPopup = (popup: Popup) => {
    setQueue((prev) => [...prev, popup]);
  };

  const hidePopup = () => {
    setIsOpen(false);

    setTimeout(() => {
      setCurrentPopup(null);
    }, 300);

    setTimeout(() => {
      if (queue.length > 0) {
        const [nextPopup, ...remainingQueue] = queue;
        setQueue(remainingQueue);
        setCurrentPopup(nextPopup);
        setIsOpen(true);
      }
    }, 350);
  };

  return (
    <PopupContext.Provider value={{ showPopup }}>
      {children}

      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={hidePopup}
        isCentered
        size={'xs'}
      >
        <AlertDialogOverlay onClick={hidePopup}>
          <AlertDialogContent textAlign={'center'} onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader
              color={POPUP_STYLES[currentPopup?.type || 'INFO'].color}
              display='flex'
              justifyContent='center'
              alignItems='center'
              gap={2}
            >
              {POPUP_STYLES[currentPopup?.type || 'INFO'].text}
              {POPUP_STYLES[currentPopup?.type || 'INFO'].icon}
            </AlertDialogHeader>
            <AlertDialogBody>{currentPopup?.message}</AlertDialogBody>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PopupContext.Provider>
  );
};

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};
